import type {IncomingMessage} from 'http';
import type {NextRequest, NextResponse} from 'next/server';

import {logger} from '@zoefin/logger';

import {
  COOKIE_DURATION,
  ENCRYPTION_KEY,
  IV_LENGTH,
  KEY_LENGTH,
  PORTAL_COOKIE_CONFIG,
  SALT,
} from './constants';

/**
 * Parses cookies from a raw cookie string (Pages Router compatible)
 */
function parseCookies(cookieHeader: string): Record<string, string> {
  const cookies: Record<string, string> = {};

  if (!cookieHeader) {
    return cookies;
  }

  cookieHeader.split(';').forEach((cookie) => {
    const [name, ...rest] = cookie.trim().split('=');

    if (name && rest.length > 0) {
      cookies[name] = decodeURIComponent(rest.join('='));
    }
  });

  return cookies;
}

/**
 * Derives a cryptographic key from the encryption key and salt
 */
async function deriveKey(): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(ENCRYPTION_KEY),
    'PBKDF2',
    false,
    ['deriveKey'],
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: encoder.encode(SALT),
      iterations: 100000,
      hash: 'SHA-256',
    },
    keyMaterial,
    {name: 'AES-GCM', length: KEY_LENGTH},
    false,
    ['encrypt', 'decrypt'],
  );
}

/**
 * Encrypts data using AES-GCM encryption (Web Crypto API)
 */
async function encrypt(text: string): Promise<string> {
  const key = await deriveKey();
  const encoder = new TextEncoder();
  const data = encoder.encode(text);

  // Generate a random IV for each encryption
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));

  const encrypted = await crypto.subtle.encrypt(
    {name: 'AES-GCM', iv},
    key,
    data,
  );

  // Combine IV and encrypted data
  const combined = new Uint8Array(iv.length + encrypted.byteLength);

  combined.set(iv);
  combined.set(new Uint8Array(encrypted), iv.length);

  // Convert to base64 for storage
  return btoa(String.fromCharCode(...combined));
}

/**
 * Decrypts data using AES-GCM decryption (Web Crypto API)
 */
async function decrypt(encryptedData: string): Promise<string> {
  try {
    const key = await deriveKey();
    const decoder = new TextDecoder();

    // Decode from base64
    const combined = new Uint8Array(
      atob(encryptedData)
        .split('')
        .map((char) => char.charCodeAt(0)),
    );

    // Extract IV and encrypted data
    const iv = combined.slice(0, IV_LENGTH);
    const encrypted = combined.slice(IV_LENGTH);

    const decrypted = await crypto.subtle.decrypt(
      {name: 'AES-GCM', iv},
      key,
      encrypted,
    );

    return decoder.decode(decrypted);
  } catch (error) {
    logger.error({error}, 'Invalid encrypted data format or decryption failed');

    throw new Error('Invalid encrypted data format or decryption failed', {
      cause: error,
    });
  }
}

/**
 * Creates a portal user cookie with encrypted data
 */
export async function createPortalUserCookie(
  userData: Omit<PortalUserCookie, 'expiresAt'>,
): Promise<string> {
  const cookieData: PortalUserCookie = {
    ...userData,
    expiresAt: Date.now() + COOKIE_DURATION * 1000,
  };

  const jsonData = JSON.stringify(cookieData);

  return encrypt(jsonData);
}

/**
 * Parses and validates a portal user cookie
 */
export async function parsePortalUserCookie(
  cookieValue: string,
): Promise<PortalUserCookie | null> {
  try {
    const decryptedData = await decrypt(cookieValue);
    const cookieData: PortalUserCookie = JSON.parse(decryptedData);

    // Validate cookie hasn't expired
    if (Date.now() > cookieData.expiresAt) {
      return null;
    }

    // Validate required fields
    if (!cookieData.userId || !cookieData.email) {
      return null;
    }

    return cookieData;
  } catch (error) {
    logger.error({error}, 'Error parsing portal user cookie');

    return null;
  }
}

/**
 * Sets a portal user cookie in the response (Server-side)
 */
export async function setPortalUserCookie(
  response: NextResponse,
  userData: Omit<PortalUserCookie, 'expiresAt'>,
): Promise<void> {
  const cookieValue = await createPortalUserCookie(userData);

  response.cookies.set({
    name: PORTAL_COOKIE_CONFIG.name,
    value: cookieValue,
    ...PORTAL_COOKIE_CONFIG,
    maxAge: COOKIE_DURATION,
  });
}

/**
 * Gets portal user cookie from request (Server-side)
 * Compatible with both App Router (NextRequest) and Pages Router (IncomingMessage)
 */
export async function getPortalUserCookie(
  request: NextRequest | IncomingMessage,
): Promise<PortalUserCookie | null> {
  let cookieValue: string | undefined;

  // Check if it's a NextRequest (App Router)
  if ('cookies' in request && typeof request.cookies.get === 'function') {
    cookieValue = (request as NextRequest).cookies.get(
      PORTAL_COOKIE_CONFIG.name,
    )?.value;
  } else {
    // It's an IncomingMessage (Pages Router)
    const cookieHeader = (request as IncomingMessage).headers.cookie || '';
    const cookies = parseCookies(cookieHeader);

    cookieValue = cookies[PORTAL_COOKIE_CONFIG.name];
  }

  if (!cookieValue) {
    return null;
  }

  return parsePortalUserCookie(cookieValue);
}

/**
 * Minimal shape of a cookie store compatible with both `next/headers` `cookies()`
 * and Next's request cookies — anything that exposes a `.get(name)` returning
 * `{ value: string } | undefined`.
 */
type CookieStoreLike = {
  get: (name: string) => {value: string} | undefined;
};

/**
 * Gets portal user cookie from a cookie store (App Router server components / route handlers).
 * Pass the result of `await cookies()` from `next/headers`.
 */
export async function getPortalUserCookieFromCookies(
  cookieStore: CookieStoreLike,
): Promise<PortalUserCookie | null> {
  const cookieValue = cookieStore.get(PORTAL_COOKIE_CONFIG.name)?.value;

  if (!cookieValue) {
    return null;
  }

  return parsePortalUserCookie(cookieValue);
}

/**
 * Returns true when the portal user cookie exists and has not yet expired.
 * Encapsulates the `Date.now()` call so callers (server pages, route handlers)
 * stay pure as far as React Compiler is concerned.
 */
export function isPortalUserAuthenticated(
  portalUser: PortalUserCookie | null,
): boolean {
  return !!portalUser && Date.now() < portalUser.expiresAt;
}

/**
 * Clears the portal user cookie (Server-side)
 */
export function clearPortalUserCookie(response: NextResponse): void {
  response.cookies.set({
    name: PORTAL_COOKIE_CONFIG.name,
    value: '',
    ...PORTAL_COOKIE_CONFIG,
    maxAge: 0,
  });
}

/**
 * Client-side cookie utilities
 */
export const clientCookieUtils = {
  /**
   * Gets portal user cookie from document.cookie (Client-side)
   */
  async get(): Promise<PortalUserCookie | null> {
    if (typeof document === 'undefined') {
      return null;
    }

    const cookies = document.cookie.split(';');
    const portalCookie = cookies.find((cookie) =>
      cookie.trim().startsWith(`${PORTAL_COOKIE_CONFIG.name}=`),
    );

    if (!portalCookie) {
      return null;
    }

    const cookieValue = portalCookie.split('=')[1];

    return parsePortalUserCookie(decodeURIComponent(cookieValue));
  },

  /**
   * Clears portal user cookie (Client-side)
   */
  clear(): void {
    if (typeof document === 'undefined') {
      return;
    }

    document.cookie = `${PORTAL_COOKIE_CONFIG.name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${PORTAL_COOKIE_CONFIG.path}`;
  },

  /**
   * Checks if user has a valid portal cookie (Client-side)
   */
  async hasValid(): Promise<boolean> {
    const cookie = await this.get();

    return cookie !== null && Date.now() < cookie.expiresAt;
  },
};

/**
 * Validates if a request has a valid portal user cookie
 */
export async function validatePortalUserRequest(
  request: NextRequest | IncomingMessage,
): Promise<{
  isValid: boolean;
  user: PortalUserCookie | null;
  error?: string;
}> {
  const userCookie = await getPortalUserCookie(request);

  if (!userCookie) {
    return {
      isValid: false,
      user: null,
      error: 'No portal user cookie found',
    };
  }

  if (Date.now() > userCookie.expiresAt) {
    return {
      isValid: false,
      user: null,
      error: 'Portal user cookie has expired',
    };
  }

  return {
    isValid: true,
    user: userCookie,
  };
}

export function getPortalUserAssets(
  assets:
    | Asset[]
    | Record<
        string,
        {
          total: string;
          asset_type_id: string;
        }
      >,
): Asset[] {
  if (!assets) {
    return [];
  }

  // validate if assets is an array
  if (Array.isArray(assets)) {
    return assets;
  }

  return Object.values(assets).map((asset) => ({
    total: parseInt(asset.total, 10),
    asset_type_id: parseInt(asset.asset_type_id, 10),
  }));
}
